import React from 'react';
import { Monitor, Sun, Moon, Globe } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { AppSettings } from '@/types';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';

export const AppearanceSettings: React.FC = () => {
  const { settings, updateSettings } = useAppStore();
  const { t } = useTranslation();

  const languageOptions = [
    { value: 'ru', label: t.languages.ru, code: 'RU' },
    { value: 'en', label: t.languages.en, code: 'EN' },
  ] as const;

  const themeOptions = [
    { value: 'light', label: t.settings.themeLight, icon: Sun },
    { value: 'dark', label: t.settings.themeDark, icon: Moon },
    { value: 'system', label: t.settings.themeSystem, icon: Monitor },
  ] as const;

  const fontSizeOptions = [
    { value: 'small', label: `${t.settings.fontSizeSmall} (12px)` },
    { value: 'medium', label: `${t.settings.fontSizeMedium} (14px)` },
    { value: 'large', label: `${t.settings.fontSizeLarge} (16px)` },
  ] as const;

  const codeThemeOptions = [
    { value: 'vs-dark', label: t.settings.codeThemeVsDark },
    { value: 'github', label: t.settings.codeThemeGithub },
    { value: 'monokai', label: t.settings.codeThemeMonokai },
    { value: 'dracula', label: t.settings.codeThemeDracula },
  ];

  const handleSettingChange = <K extends keyof AppSettings>(
    key: K,
    value: AppSettings[K]
  ) => {
    updateSettings({ [key]: value });
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          {t.settings.appearance}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t.settings.customizeLookAndFeel}
        </p>
      </div>

      {/* Language Selection */}
      <div className="space-y-3">
        <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center gap-2">
          <Globe className="h-5 w-5" />
          {t.settings.language}
        </h4>
        <div className="grid grid-cols-2 gap-3" role="group" aria-label={t.settings.language}>
          {languageOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-label={option.label}
              aria-pressed={settings.language === option.value}
              onClick={() => handleSettingChange('language', option.value)}
              className={clsx(
                'flex items-center justify-center gap-3 p-4 border rounded-lg transition-colors',
                settings.language === option.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
              )}
            >
              <span className="text-sm font-semibold tracking-wide">{option.code}</span>
              <span className="text-sm font-medium">{option.label}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
          {t.settings.languageAffectsLLM}
        </p>
      </div>

      {/* Theme Selection */}
      <div className="space-y-3">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">
          {t.settings.theme}
        </h4>
        <div className="grid grid-cols-3 gap-3" role="group" aria-label={t.settings.theme}>
          {themeOptions.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                type="button"
                aria-label={option.label}
                aria-pressed={settings.theme === option.value}
                onClick={() => handleSettingChange('theme', option.value)}
                className={clsx(
                  'flex flex-col items-center p-4 border rounded-lg transition-colors',
                  settings.theme === option.value
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                )}
              >
                <Icon className="h-6 w-6 mb-2" />
                <span className="text-sm font-medium">{option.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Font Size */}
      <div className="space-y-3">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">
          {t.settings.fontSize}
        </h4>
        <div className="space-y-2">
          {fontSizeOptions.map((option) => (
            <label
              key={option.value}
              className="flex items-center space-x-3 cursor-pointer"
            >
              <input
                type="radio"
                name="fontSize"
                value={option.value}
                checked={settings.fontSize === option.value}
                onChange={(e) => handleSettingChange('fontSize', e.target.value as any)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {option.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Code Theme */}
      <div className="space-y-3">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">
          {t.settings.codeTheme}
        </h4>
        <select
          value={settings.codeTheme}
          onChange={(e) => handleSettingChange('codeTheme', e.target.value)}
          className="w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
        >
          {codeThemeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Other Settings */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">
          {t.settings.chatPreferences}
        </h4>
        
        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.showTimestamps}
              onChange={(e) => handleSettingChange('showTimestamps', e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t.settings.showMessageTimestamps}
            </span>
          </label>
          
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.autoSave}
              onChange={(e) => handleSettingChange('autoSave', e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t.settings.autoSaveChatSessions}
            </span>
          </label>
          
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.enableNotifications}
              onChange={(e) => handleSettingChange('enableNotifications', e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t.settings.enableDesktopNotifications}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};